import { HttpClient, HttpRequest, provideHttpClient, withInterceptorsFromDi } from "@angular/common/http";
import { enableProdMode, ErrorHandler, inject, provideAppInitializer } from "@angular/core";
import '@angular/localize/init';
import { bootstrapApplication } from "@angular/platform-browser";
import { provideAnimations } from "@angular/platform-browser/animations";
import { provideRouter, withComponentInputBinding, withEnabledBlockingInitialNavigation } from "@angular/router";
import { ContentViewerComponent } from "@app/components/content-viewer/content-viewer.component";
import { apiAccesses, interceptorProvider } from "@app/msal/msalb2c.config";
import { CatalogMobileDetailsComponent } from "@app/public/catalog/catalog-mobile-details/catalog-mobile-details.component";
import { CatalogMobileInfoComponent } from "@app/public/catalog/catalog-mobile-info/catalog-mobile-info.component";
import { CatalogMobileComponent } from "@app/public/catalog/catalog-mobile/catalog-mobile.component";
import { CatalogComponent } from "@app/public/catalog/catalog.component";
import { CatalogInfoViewerComponent } from "@app/viewers/cataloginfoviewer/cataloginfoviewer.component";
import { HtmlviewerComponent } from "@app/viewers/htmlviewer/htmlviewer.component";
import { OverviewViewerComponent } from "@app/viewers/overview-viewer/overview-viewer.component";
import { interactionType, loginLanguage, profEditAuthority, protectedResourceMap, pwdResetAuthority, scopes, signinAuthority } from "@commonConfig/msalb2c.config";
import { MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG, MsalBroadcastService, MsalGuard, MsalInterceptorAuthRequest, MsalService } from "@commonNodeModules/@azure/msal-angular";
import { BrowserCacheLocation, LogLevel, PublicClientApplication } from "@commonNodeModules/@azure/msal-browser";
import { provideTranslateService, TranslateCompiler } from "@commonNodeModules/@ngx-translate/core";
import { provideTranslateHttpLoader } from "@commonNodeModules/@ngx-translate/http-loader";
import { TranslateMessageFormatCompiler } from "@commonNodeModules/ngx-translate-messageformat-compiler";
import { MsalB2CService } from "@commonServices/msalb2c.service";
import { environment } from "@environments/environment";
import { globalEnvironment } from "@environments/globalEnvironment";
import { Client as AccountPublicApi } from "@interfaces/HttpClient/AccountApiPublicModels";
import { Client as AnalyticsTrackingApi } from "@interfaces/HttpClient/AnalyticsApiTrackingModels";
import { Client as CatalogPublicAPI } from "@interfaces/HttpClient/CatalogApiPublicModels";
import { ApplicationInsightsService } from "@services/application-insights.service";
import { CacheService } from "@services/cache.service";
import { CssVariableService } from "@services/css-variable.service";
import { ErrorHandlerService } from "@services/error-handler.service";
import { GlobalVarService } from "@services/global-var.service";
import { IntervalService } from "@services/interval.service";
import { SoftwareIntegrationCallbackService } from "@services/software-integration-callback.service";
import { SoftwarepartnerApiService } from "@services/softwarepartner-api.service";
import { TrackEventService } from "@services/track-event.service";
import { TranslationService } from "@services/translation.service";
import { UserAuthorizationService } from "@services/user-authorization.service";
import { ViewTypeService } from "@services/view-type.service";
import { WatchlistActionService } from "@services/watchlist-action.service";
import { WatchlistService } from "@services/watchlist.service";
import { firstValueFrom } from "rxjs";
import { AppComponent } from "./app/app.component";
import { routes } from "./app/routes";

export const getBaseUrl = () => document.getElementsByTagName("base")[0].href

const providers = [{ provide: "BASE_URL", useFactory: getBaseUrl, deps: [] }];

if (environment.production) {
	enableProdMode();
}

// SEO-relevante Komponenten vorladen
bootstrapApplication(AppComponent, {
	providers: [
		providers,
		CatalogComponent,
		CatalogMobileComponent,
		CatalogMobileDetailsComponent,
		CatalogMobileInfoComponent,
		CatalogInfoViewerComponent,
		HtmlviewerComponent,
		ContentViewerComponent,
		OverviewViewerComponent,
		// Kendo braucht das leider noch. 
		// TODO: provideAnimations entfernen wenn sich bei Kendo dort mal jemand angesprochen fühlt seine Arbeit zu machen
		provideAnimations(),
		provideRouter(routes, withEnabledBlockingInitialNavigation(), withComponentInputBinding()),
		WatchlistActionService,
		ViewTypeService,
		WatchlistService,
		TrackEventService,
		{ provide: ErrorHandler, useClass: ErrorHandlerService },
		CssVariableService,
		UserAuthorizationService,
		GlobalVarService,
		CacheService,
		IntervalService,
		ApplicationInsightsService,
		MsalB2CService,
		provideAppInitializer(() => {
			const initializerFn = ((msalService: MsalService) => async () => { await firstValueFrom(msalService.handleRedirectObservable()); await firstValueFrom(msalService.initialize()) })(inject(MsalService));
			return initializerFn();
		}),
		provideHttpClient(),
		provideTranslateService({
			lang: 'de',
			fallbackLang: 'de',
			loader: provideTranslateHttpLoader({
				prefix: '/assets/i18n/',
				suffix: '.json?buildDate=' + (new Date(globalEnvironment.buildDate))?.getTime()
			}),
			compiler: {
				provide: TranslateCompiler,
				useClass: TranslateMessageFormatCompiler
			}
		}),
		provideAppInitializer(() => {
			const initializerFn = ((intervalService: IntervalService) => () => intervalService.init())(inject(IntervalService));
			return initializerFn();
		}),
		provideAppInitializer(() => {
			const initializerFn = ((softwarepartnerApiService: SoftwarepartnerApiService) => () => { softwarepartnerApiService.init() })(inject(SoftwarepartnerApiService));
			return initializerFn();
		}),
		provideAppInitializer(() => {
			const initializerFn = ((viewTypeService: ViewTypeService) => () => viewTypeService.initializeViewTypeByWidthSubscription())(inject(ViewTypeService));
			return initializerFn();
		}),

		{
			provide: AccountPublicApi,
			useFactory: (http: HttpClient) => new AccountPublicApi(http, environment.accountApi),
			deps: [HttpClient]
		},
		{
			provide: AnalyticsTrackingApi,
			useFactory: (http: HttpClient) => new AnalyticsTrackingApi(http, environment.analyticsApi),
			deps: [HttpClient]
		},
		provideAppInitializer(() => {
			const initializerFn = ((softwareIntegrationCallbackService: SoftwareIntegrationCallbackService) => async () => { await softwareIntegrationCallbackService.startSession(); })(inject(SoftwareIntegrationCallbackService));
			return initializerFn();
		}),
		// AnalyticsTrackingService has to be initialized after SoftwareIntegrationCallbackService, to make shure that softwareIntegrationSession already exists, if applicable
		provideAppInitializer(() => {
			const initializerFn = ((cacheService: CacheService) => async () => { await cacheService.checkForUpdates(); })(inject(CacheService));
			return initializerFn();
		}),
		provideAppInitializer(() => {
			const initializerFn = ((translationService: TranslationService) => async () => { await translationService.initTranslation(); })(inject(TranslationService));
			return initializerFn();
		}),
		provideAppInitializer(() => {
			const initializerFn = ((userAutorisationService: UserAuthorizationService) => () => userAutorisationService.subscribeState())(inject(UserAuthorizationService));
			return initializerFn();
		}),
		{
			provide: CatalogPublicAPI,
			useFactory: (http: HttpClient) => new CatalogPublicAPI(http, environment.catalogApi),
			deps: [HttpClient]
		},
		interceptorProvider,
		provideHttpClient(withInterceptorsFromDi()),
		{
			provide: MSAL_INSTANCE,
			useFactory: () => new PublicClientApplication({
				auth: {
					authority: signinAuthority,
					knownAuthorities: [
						signinAuthority,
						pwdResetAuthority,
						profEditAuthority
					],
					clientId: environment.msal.clientId,
					redirectUri: window.location.origin,
					postLogoutRedirectUri: window.location.href,
					navigateToLoginRequestUrl: true
				},
				cache: {
					cacheLocation: BrowserCacheLocation.LocalStorage,
					storeAuthStateInCookie: false,
				},
				system: {
					loggerOptions: {
						logLevel: LogLevel.Verbose,
						piiLoggingEnabled: true,
					},
					allowRedirectInIframe: true,
					allowPlatformBroker: false,
				}
			})
		},
		{
			provide: MSAL_GUARD_CONFIG,
			useFactory: () => ({
				interactionType,
				authRequest: () => ({ scopes, extraQueryParameters: { ui_locales: loginLanguage() } })
			})
		},
		{
			provide: MSAL_INTERCEPTOR_CONFIG,
			useFactory: () => ({
				protectedResourceMap: protectedResourceMap(apiAccesses),
				interactionType,
				authRequest: ((msalService: MsalService, req: HttpRequest<unknown>, originalAuthRequest: MsalInterceptorAuthRequest) => ({
					...originalAuthRequest,
					extraQueryParameters: { ui_locales: loginLanguage() }
				}))
			}),
			deps: [],
		},
		MsalService,
		MsalGuard,
		MsalBroadcastService
	]
})
	.catch(err => console.log(err));
